import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TylerMatTableDataSource } from '../../angular-material/extended.class';
import { DataQueryService } from '../../../classroom360/data-query/data-query.service';
import { DataQueryFieldFilter } from '../../../classroom360/data-query/data-query-field-filter.model';
import { Subscription } from 'rxjs';
import { DataQuery } from '../../../classroom360/data-query/data-query.model';
import { DataQueryField } from '../../../classroom360/data-query/data-query-field.model';
import { LoginService } from '../../../shared-modules/login/login.service';
import { LookupService } from '../../lookup/lookup.service';
import { YearService } from '../../year/year.service';
import { GeneralPreferences } from '../../user-preferences/general-preferences.model';
import { UserPreferencesService } from '../../user-preferences/user-preferences.service';
import { AdvancedFilterComponent } from '../../advanced-filter/advanced-filter.component';

@Component({
    selector: 'app-advanced-student-search',
    templateUrl: './advanced-student-search.component.html',
    styleUrls: ['./advanced-student-search.component.css']
})
export class AdvancedStudentSearchComponent extends AdvancedFilterComponent implements OnInit, OnDestroy {

    // Injected Properties
    initialStudentNumber = '';
    initialFirstName = '';
    initialLastName = '';
    initialGrade = '';
    initialEnrollment = '';
    initialEnrollDate = '';
    initialDOB = '';
    initialSchool = '';
    initialStatus = '';

    // Properties
    tableData = new TylerMatTableDataSource();
    subscriptions: Subscription[] = [];
    // information: DataQueryInformation;
    // preferences: DataQueryPreferences;
    generalPreferences: GeneralPreferences;
    allFilters: DataQueryFieldFilter[] = [];
    V10: boolean;

    // Class Functions
    constructor(
        @Inject(MAT_DIALOG_DATA) private data: any,
        dialogBox: MatDialogRef<AdvancedStudentSearchComponent>,
        dataQueryService: DataQueryService,
        loginService: LoginService,
        lookupService: LookupService,
        yearService: YearService,
        userPreferencesService: UserPreferencesService,
        dialog: MatDialog
    ) {
        super(dataQueryService, userPreferencesService, loginService, dialogBox, lookupService, yearService, dialog);
    }

    // Event Functions
    addRosterFilter() {
        // PRE: the add roster filter button is clicked
        // POST: opens a dialog to select a number of courses. Then, adds those courses as a filter.
        this.dataQueryService.addRosterFilter(null, () => {

            // Re-establishing filter fields
            this.setAllFilters();

            // Setting table data
            this.tableData.data = this.allFilters;
        });
    }

    // Overridden Methods
    initialize() {
        // POST: initializes this component for staff search

        // Setting the injected properties
        if (this.data) {
            this.initialStudentNumber = this.data.initialStudentNumber;
            this.initialFirstName = this.data.initialFirstName;
            this.initialLastName = this.data.initialLastName;
            this.initialGrade = this.data.initialGrade;
            this.initialEnrollment = this.data.initialEnrollment;
            this.initialEnrollDate = this.data.initialEnrollDate;
            this.initialDOB = this.data.initialDOB;
            this.initialSchool = this.data.initialSchool;
            this.initialStatus = this.data.initialStatus;
        }

        // Undoing our current query to ensure no crossover from elsewhere
        if (!this.dataQueryService.isStudentSearch) {
            this.dataQueryService.currentQuery = new DataQuery();
            this.dataQueryService.isStudentSearch = true;
        }
    }

    setValueSwitch(field: DataQueryField, filter: DataQueryFieldFilter, updateUsing: boolean) {
        // POST: sets the values for the student search filters
        if (updateUsing) {
            filter.UseFilterForStudentSearch = false;
        }
        let val = '';

        // Updating based on grid filters (these do not take priority, so they come first)
        switch (field.Alias) {
            case 'Student ID':
            case 'Student Number':
                filter.Value = this.initialStudentNumber;
                break;
            case 'Name - First':
            case 'First Name':
                filter.Value = this.initialFirstName;
                break;
            case 'Name - Last':
            case 'Last Name':
                filter.Value = this.initialLastName;
                break;
            case 'Birth Date':
            case 'DOB':
                if (this.initialDOB) {
                    const dateOfBirth = new Date(this.initialDOB);
                    if (dateOfBirth.toString() != 'Invalid Date') {
                        filter.Value = dateOfBirth.toString();
                    }
                }
                break;
            case 'Grade':
                filter.Value = this.initialGrade;
                break;
            case 'School':
                filter.Value = this.initialSchool;
                break;
            case 'Enrollment Date':
                if (this.initialEnrollDate) {
                    const enrollmentDate = new Date(this.initialEnrollDate);
                    if (enrollmentDate.toString() != 'Invalid Date') {
                        filter.Value = enrollmentDate.toString();
                    }
                }
                break;
            default:
                break;
        }

        // Updating based on search criteria (these take priority, so they come last)
        if (this.loginService.LoginSettings.V10) {
            if (field.FieldName == 'Student_ID') {
                field.Alias = 'StudentId';
            }
            switch (field.FieldName.toLowerCase()) {
                case 'student_number':
                    if (this.dataQueryService.studentSearchFilters.StudentNumber) {
                        filter.Value = this.dataQueryService.studentSearchFilters.StudentNumber;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'student_id':
                    field.Alias = 'StudentId';
                    filter.hidden = true;
                    break;
                case 'first_name':
                    filter.Operator = 'Starts With';
                    if (this.dataQueryService.studentSearchFilters.FirstName) {
                        filter.Value = this.dataQueryService.studentSearchFilters.FirstName;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'last_name':
                    filter.Operator = 'Starts With';
                    if (this.dataQueryService.studentSearchFilters.LastName) {
                        filter.Value = this.dataQueryService.studentSearchFilters.LastName;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'grade_level':
                    field.Alias = 'Grade';
                    if (this.dataQueryService.studentSearchFilters.Grade) {
                        filter.Value = this.dataQueryService.studentSearchFilters.Grade;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'school_name':
                    field.Alias = 'School';
                    val = this.dataQueryService.studentSearchFilters.EnrollmentSchool == null ? '' : this.dataQueryService.studentSearchFilters.EnrollmentSchool;
                    if (val == '') {
                        val = this.dataQueryService.studentSearchFilters.ddlSchool == null ? '' : this.dataQueryService.studentSearchFilters.ddlSchool;
                    }

                    if (val != 'All' && val != '(All)' && val != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                        filter.Value = val;
                    }

                    break;
                case 'primary':
                    field.Alias = 'Enrollment';
                    val = 'All';
                    if (this.dataQueryService.studentSearchFilters.Enrollment) {
                        val = this.dataQueryService.studentSearchFilters.Enrollment;
                    }
                    if (val != 'All' && val != '(All)' && val != '*' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                        filter.Value = val;
                    }

                    break;
                case 'entry_date':
                    field.Alias = 'Enrollment Date';
                    if (this.dataQueryService.studentSearchFilters.EnrollmentDate) {
                        filter.Value = this.dataQueryService.studentSearchFilters.EnrollmentDate;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'enrollment_status':
                    field.Alias = 'Status';
                    val = this.dataQueryService.studentSearchFilters.EnrollmentStatus == null ? '' : this.dataQueryService.studentSearchFilters.EnrollmentStatus;
                    if (val == '') {
                        val = this.dataQueryService.studentSearchFilters.rbStatus == null ? '' : this.dataQueryService.studentSearchFilters.rbStatus;
                    }
                    if (val != 'All' && val != '(All)' && val != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                        filter.Value = val;
                    }
                    break;
                case 'dob':
                    if (this.dataQueryService.studentSearchFilters.DOB) {
                        filter.Value = this.dataQueryService.studentSearchFilters.DOB;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
            }
        } else {
            switch (field.DisplayName) {
                case 'lngStudentId':
                case 'Primary':
                    filter.hidden = true;
                    break;
                case 'Student ID':
                    if (this.dataQueryService.studentSearchFilters.StudentNumber) {
                        filter.Value = this.dataQueryService.studentSearchFilters.StudentNumber;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'Name - First':
                    if (updateUsing || !filter.Operator || filter.Operator == '') {
                        filter.Operator = 'Starts With';
                    }
                    if (this.dataQueryService.studentSearchFilters.FirstName) {
                        filter.Value = this.dataQueryService.studentSearchFilters.FirstName;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'Name - Last':
                    if (updateUsing || !filter.Operator || filter.Operator == '') {
                    filter.Operator = 'Starts With';
                    }
                    if (this.dataQueryService.studentSearchFilters.LastName) {
                        filter.Value = this.dataQueryService.studentSearchFilters.LastName;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'Grade':
                    if (this.dataQueryService.studentSearchFilters.Grade) {
                        filter.Value = this.dataQueryService.studentSearchFilters.Grade;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'Primary Enrollment Site Name':
                    val = this.dataQueryService.studentSearchFilters.EnrollmentSchool == null ? '' : this.dataQueryService.studentSearchFilters.EnrollmentSchool;
                    if (val == '') {
                        val = this.dataQueryService.studentSearchFilters.ddlSchool == null ? '' : this.dataQueryService.studentSearchFilters.ddlSchool;
                    }
                    if (val != 'All' && val != '(All)' && val != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                        filter.Value = val;
                    }
                    break;
                case 'Enrollment Date':
                    if (this.dataQueryService.studentSearchFilters.EnrollmentDate) {
                        filter.Value = this.dataQueryService.studentSearchFilters.EnrollmentDate;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
                case 'Enrollment Status':
                    val = this.dataQueryService.studentSearchFilters.EnrollmentStatus == null ? '' : this.dataQueryService.studentSearchFilters.EnrollmentStatus;
                    if (val == '') {
                        val = this.dataQueryService.studentSearchFilters.rbStatus == null ? '' : this.dataQueryService.studentSearchFilters.rbStatus;
                    }
                    if (val != 'All' && val != '(All)' && val != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                        filter.Value = val;
                    }
                    if (!filter.Value) {
                        filter.Value = 'All';
                    }
                    break;
                case 'Birth Date':
                    if (this.dataQueryService.studentSearchFilters.DOB) {
                        filter.Value = this.dataQueryService.studentSearchFilters.DOB;
                    }
                    if (filter.Value && filter.Value != '' && updateUsing) {
                        filter.UseFilterForStudentSearch = true;
                    }
                    break;
            }
        }
    }

    setFilterPropertiesSwitch(field: DataQueryField, filter: DataQueryFieldFilter) {
        // POST: sets the filter's properties
        filter.Operator = '=';
        filter.Value = '';
        filter.Value2 = '';
        filter.Group = 'A';
        filter.DisplayMenu = false;
        filter.UseCurrentDate1 = false;
        filter.UseCurrentDate2 = false;
        filter.UseFilterForStudentSearch = true;
        filter.IsStudentFilter = false;
        filter.StudentFilterId = 0;
        filter.ApplyToAll = false;
    }
}
